import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  faTrashCan,
  faClockRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useArchiveFilter,
  useBatchSelection,
  type BatchAction,
} from "@components/CatalogIndex";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useUserDefinedFieldsList } from "../api/useUserDefinedFieldsList";
import { useUserDefinedFieldMutations } from "../api/useUserDefinedFieldMutations";
import { userDefinedFieldColumns } from "./columns";

type PendingAction = "archive" | "restore" | "purge" | null;

export const List = () => {
  const navigate = useNavigate();
  const { tab, setTab, isArchivedFilter, tabsFor } = useArchiveFilter();
  const { rows, totalCount, isLoading, isFetching } = useUserDefinedFieldsList({
    archived: isArchivedFilter,
  });
  const archivedQuery = useUserDefinedFieldsList({
    archived: !isArchivedFilter,
  });
  const { destroy, move, restore, purge } = useUserDefinedFieldMutations();

  const { selectedIds, setSelectedIds, clear } = useBatchSelection([tab]);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [toast, setToast] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);

  if (isLoading) return <ScopeStackSpinner />;

  const runBatch = async (
    fn: (id: string) => Promise<unknown>,
    successText: string,
    failText: string
  ) => {
    const ids = [...selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await fn(id);
      } catch {
        failures += 1;
      }
    }
    if (failures === 0) {
      setToast({
        variant: "success",
        text: successText.replace("{count}", String(ids.length)),
      });
    } else {
      setToast({
        variant: "error",
        text: failText
          .replace("{failures}", String(failures))
          .replace("{count}", String(ids.length)),
      });
    }
    clear();
    setPendingAction(null);
  };

  const handleConfirm = async () => {
    if (pendingAction === "archive") {
      await runBatch(
        (id) => destroy(id),
        "{count} field(s) archived.",
        "Failed to archive {failures} of {count} fields."
      );
    } else if (pendingAction === "restore") {
      await runBatch(
        (id) => restore(id),
        "{count} field(s) restored.",
        "Failed to restore {failures} of {count} fields."
      );
    } else if (pendingAction === "purge") {
      await runBatch(
        (id) => purge(id),
        "{count} field(s) permanently deleted.",
        "Failed to permanently delete {failures} of {count} fields."
      );
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    // Serialize: the API's set_list_position! cascades neighbor shifts on
    // each call, so parallel moves race for the unique-position window.
    // Moves arrive sorted by ascending target position, which lets
    // acts_as_list compact sparse positions into 1..N.
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
    } catch {
      setToast({
        variant: "error",
        text: "Failed to reorder. The list has been refreshed.",
      });
    }
  };

  const counts = isArchivedFilter
    ? { active: archivedQuery.totalCount, archived: totalCount }
    : { active: totalCount, archived: archivedQuery.totalCount };

  const activeActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Archive",
      onClick: () => setPendingAction("archive"),
    },
  ];

  const archivedActions: BatchAction[] = [
    {
      icon: faClockRotateLeft,
      label: "Restore",
      onClick: () => setPendingAction("restore"),
    },
    {
      icon: faTrashCan,
      label: "Permanently delete",
      onClick: () => setPendingAction("purge"),
      variant: "danger",
    },
  ];

  const batchActions = isArchivedFilter ? archivedActions : activeActions;

  return (
    <>
      <AdminListCard
        title="User Defined Fields"
        onNew={() => navigate("./new")}
        newButtonLabel="+ Add Field"
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => setTab(key as "active" | "archived")}
          tabs={tabsFor(counts)}
        />
        <AdminListTable
          columns={userDefinedFieldColumns}
          data={rows}
          isLoading={isFetching}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          draggable={!isArchivedFilter}
          onReorder={!isArchivedFilter ? handleReorder : undefined}
          onEditHref={(row) => `/admin/project_variables/${row.id}/edit`}
          editLinkDisabled={isArchivedFilter}
          emptyMessage={
            isArchivedFilter
              ? "No archived fields."
              : "No user-defined fields yet — add one to get started."
          }
        />
      </AdminListCard>
      <ConfirmDeleteModal
        open={pendingAction === "archive"}
        title={`Delete field? (${selectedIds.length})`}
        message={`Move ${selectedIds.length} field(s) to Archived?`}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDeleteModal
        open={pendingAction === "restore"}
        title={`Restore field? (${selectedIds.length})`}
        message={`Restore ${selectedIds.length} field(s) to Active?`}
        confirmLabel="Restore"
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDeleteModal
        open={pendingAction === "purge"}
        title={`Permanently delete field? (${selectedIds.length})`}
        message={`Permanently delete ${selectedIds.length} field(s)? This cannot be undone.`}
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
